<script lang="ts" setup>
import UiCardAccordion from '@boats/ui/components/UiCardAccordion.vue'
import UiCardCart from '@boats/ui/components/UiCardCart.vue'


const currency = useCurrencyStore()
const cart = useCartStore()
const modals = useModalsStore()
const config = useRuntimeConfig()
const tickets = ref<Record<string, string[]>>({})
const selectedSeatCount = ref(0)
const { $toast, $i18n } = useNuxtApp()
const { t } = $i18n

function combineTickets(selection) {
  const grouped: Record<string, string[]> = {}

  selection.forEach((item) => {
    const key = item.FP
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(item.Tid)
  })

  return grouped
}

const ltdEvents = {
  'LTD.Basket.OnSubmit': async () => {
    if (!modals.seatWidget) return

    await $toast.promise(
      cart.addToCart({
        productId: modals.seatWidget.product.id,
        performanceId: modals.seatWidget?.performanceId,
        variations: Object.entries(tickets.value).map(([category, ids]) => ({
          quantity: ids.length,
          id: category,
          tickets: ids
        }))
      }),
      {
        pending: t('feedback.cart.adding'),
        success: t('feedback.cart.added')
      }
    )

    modals.closeSeatWidgetLtdModal()
  },
  'LTD.SeatPlan.OnSeatSelected': async (e) => {
    await nextTick()
    selectedSeatCount.value = e.detail.selection.length
    tickets.value = combineTickets(e.detail.selection)
  },
  'LTD.SeatPlan.OnSeatUnselected': async (e) => {
    await nextTick()
    selectedSeatCount.value = e.detail.selection.length
    tickets.value = combineTickets(e.detail.selection)

    const btn = document.querySelector('.ltd-basket .app-button');
    if (e.detail.selection.length === 0) {
      setTimeout(() => {
        if (btn) btn.setAttribute('disabled', '');
      }, 200);
    }
  },
  'LTD.SeatPlan.OnDrawFinished': async () => {
    const zoomers = document.querySelectorAll('.ltd-seatplan__zoomer');
    if (zoomers.length >= 2) {
      zoomers[1].style.display = 'none';
    }
  }
}

const quantity = computed(() => {
  return selectedSeatCount.value
})

function subscribeLtdEvents() {
  Object.entries(ltdEvents).forEach(([event, handler]) => {
    document.addEventListener(event, handler)
  })
}

function unsubscribeLtdEvents() {
  Object.entries(ltdEvents).forEach(([event, handler]) => {
    document.removeEventListener(event, handler)
  })
}

function cleanUpLtdDOM() {
  document.querySelector('.ltd-seatplan')?.replaceChildren()
  document.querySelector('.ltd-legend')?.replaceChildren()
  document.querySelector('.ltd-basket')?.replaceChildren()
}

function reloadLTD(): Promise<void> {
  return new Promise((resolve) => {
    const existingScript = document.getElementById(config.public.ltd.script_id)
    if (existingScript) {
      existingScript.remove()
    }
    document.querySelectorAll('script').forEach((script) => {
      if (script.src.includes('finale-cdn.uk')) {
        script.remove()
      }
    })

    const script = document.createElement('script')
    script.id = config.public.ltd.script_id
    script.src = config.public.ltd.script
    script.onload = () => resolve()
    document.head.appendChild(script)
  })
}

const calculateSeatPrice = (price) => {
  price = price / 100 * modals.seatWidget.product.extra.exchangeRate;
  price += (price * modals.seatWidget.product.extra.margin * 0.01);
  return customCeil(price * 100, currency.roundUp);
}

function customCeil(price, roundTo) {
  const ceilPrice = Math.ceil(price);
  if (ceilPrice % roundTo === 0) {
    return ceilPrice;
  } else {
    return Math.round((price + roundTo / 2) / roundTo) * roundTo;
  }
}

onMounted(() => {
  window.BrevoConversations?.hide()
  window.LTD.SeatPlan.init({
    clientId: config.public.ltd.clientId,
    performanceId: modals.seatWidget.performanceExternalId,
    sandbox: !config.public.isProduction,
    limitSelectedSeats: Number(modals.seatWidget.product.extra.maxReservationTicketCount ?? 2) - 1,
    behavior: {
      formatPrice: (price: number): string => formatCurrency(price),
      convertPriceValue: calculateSeatPrice
    },
    event: {
      scrollZoom: false,
      scrollMove: true,
    },
    i18n: {
      "faceValueShort": 'FV',
      "noFee": t('ltd.noFee'),
      "save": t('ltd.save'),
      "descPrefix": t('ltd.descPrefix'),
      "legend": {
        "all": t('ltd.legend.all'),
        "noFees": t('ltd.legend.noFees'),
        "offer": t('ltd.legend.offer'),
      },
      "basket": {
        "addSingle": t('ltd.basket.addSingle'),
        "addMultiple": t('ltd.basket.addMultiple'),
        "addGrand": t('ltd.basket.addGrand'),
        "addGrandBreakpoint": 0,
        "add": t('ltd.basket.add'),
      },
    },
    filterPriceBands: true,
    margin: 20
  })
  subscribeLtdEvents()
})

onBeforeUnmount(() => {
  window.BrevoConversations?.show()
  unsubscribeLtdEvents()
  delete window.LTD
  cleanUpLtdDOM()
  reloadLTD()
})


</script>

<template>
  <div v-if="modals.seatWidget" class="app-modal-seat-widget-ltd-content">
    <div class="seat-widget-content">
      <div class="widget-sidebar">
        <UiCardAccordion
          :title="`${$t('string.Event-tickets')} (x${quantity})`"
          class="pointer-events-auto"
        >
          <UiCardCart
            :title="modals.seatWidget.product.name"
            :image="{ src: modals.seatWidget.product.image }"
          >
            <p>
              {{
                [
                  modals.seatWidget.date
                    ? formatDate(modals.seatWidget.date)
                    : undefined,
                  modals.seatWidget.date && modals.seatWidget.time
                    ? formatTime(
                      [modals.seatWidget.date, modals.seatWidget.time].join(
                        ' '
                      )
                    )
                    : undefined
                ]
                  .filter(Boolean)
                  .join(', ')
              }}
            </p>
            <AppButton variant="text" @click="modals.closeSeatWidgetLtdModal">
              {{ $t('string.Close') }}
            </AppButton>
          </UiCardCart>
        </UiCardAccordion>
        <p class="ltd-seatplan__preloader">{{ $t('ltd.preparing') }}</p>
        <div
          class="ltd-basket"
          display-tickets
          display-submit
          submit-class="app-button text-button app-button--contained app-button--full-width mt-8 green-btn"
        ></div>
      </div>
      <div class="widget-content">
        <div class="ltd-legend"></div>
        <div class="ltd-seatplan"></div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-modal-seat-widget-ltd-content {
  display: flex;
  min-height: 100%;
  width: 100%;
  background-color: $white;
  flex-direction: row;

  .app-button--contained:hover, .app-button--contained:focus {
    border: 1px solid green;
  }

  .ui-card-cart__content-text {
    display: flex;
    align-items: baseline;
    justify-content: space-between;

    .app-button {
      margin-left: 1.25rem;
    }
  }

  .ltd-seatplan__zoomer {
    flex-direction: column;
    right: unset;
    bottom: unset;
    top: 1rem !important;
    left: 1rem;
    border-radius: 0;
    position: absolute;
    display: flex;
  }

  .ltd-legend {
    margin-bottom: 2rem;
    text-align: center;
  }

  .ltd-seatplan {
    width: 100%;
    display: block;
    position: relative !important;
  }

  .ltd-basket__actions {
    margin-bottom: 1rem;
  }

  .seat-widget-content {
    pointer-events: auto;
    width: 100%;
    display: flex;
    flex-direction: row;
    background-color: white;
  }

  .widget-sidebar {
    width: 25%;
    max-width: 20rem;
    padding: 1rem;
    overflow-y: auto;
    background: #f9f9f9;
  }

  .widget-content {
    flex: 1;
    overflow: auto;
    background-color: #f9f9f9;
    padding: 1rem;
    position: relative;
  }

  @include media-breakpoint-down(sm) {
    .seat-widget-content {
      height: 100vh;
      display: flex;
      flex-direction: column;
    }

    .widget-sidebar {
      order: 1;
      flex: 0 0 auto;
      padding: 1rem;
      background: #f9f9f9;
      max-width: 100%;
      width: 100%;
      overflow-y: auto;
    }

    .widget-content {
      order: 2;
      flex: 1 1 auto;
      position: relative;
      background: #f9f9f9;
      display: flex;
      width: 100%;
      flex-direction: column;
      overflow: hidden;
      padding: 1rem 1rem 10rem 1rem;
    }

    .ltd-seatplan__zoomer {
      display: none;
    }

    .ltd-legend {
      flex: 0 0 auto;
      padding: 1rem 1rem;
      background: white;
      margin-bottom: 0;
    }

    .ltd-seatplan {
      scrollbar-width: none;
      flex: 1 1 auto;
      position: relative !important;
      overflow: auto;
    }

    .ltd-basket__wrap {
      flex-wrap: nowrap !important;
      overflow: auto !important;
    }

    .ltd-basket {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 1rem;
      background: #fff;
      box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.15);
      z-index: 10;
    }
  }
}

.green-btn {
  background: green;
  border: 1px solid green;
  color: white;
  transition: background 0.2s ease, transform 0.1s ease;
}

.green-btn:hover {
  background: green;
  border: 1px solid green;
  transform: translateY(-2px);
}

.green-btn:hover .green-btn:focus {
  border: 1px solid green;
}

.green-btn:focus {
  background: green;
  transform: scale(0.98);
}

.green-btn:active {
  background: green;
  transform: scale(0.98);
}
</style>
